import axios from "axios";
import { hideChildModal, hideModal } from "./modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
  query: { ids: [], total: null },
};

/*********/
/* TYPES */
/*********/
const FETCH_FILTERED_CONTAINERS = "FETCH_FILTERED_CONTAINERS";
const FETCH_FILTERED_CONTAINERS_SUCCESS = "FETCH_FILTERED_CONTAINERS_SUCCESS";
const FETCH_FILTERED_CONTAINERS_ERROR = "FETCH_FILTERED_CONTAINERS_ERROR";
const FETCH_NEXT_FILTERED_CONTAINERS = "FETCH_NEXT_FILTERED_CONTAINERS";
const FETCH_NEXT_FILTERED_CONTAINERS_SUCCESS = "FETCH_NEXT_FILTERED_CONTAINERS_SUCCESS";

const UPDATE_FILTERED_CONTAINER = "UPDATE_FILTERED_CONTAINER";
const UPDATE_FILTERED_CONTAINER_SUCCESS = "UPDATE_FILTERED_CONTAINER_SUCCESS";
const UPDATE_FILTERED_CONTAINER_ERROR = "UPDATE_FILTERED_CONTAINER_ERROR";

const getIdParams = (ids) =>
  ids.length > 0 ? ids.map((id) => `&id[]=${id}`).join("") : "";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getFilteredContainers =
  (ids = []) =>
  async (dispatch, getState) => {
    const validIds = ids.filter((id) => {
      if (typeof id === "string" && id.trim() !== "") return true;
      if (typeof id === "number" && Number.isInteger(id)) return true;
      return false;
    });

    if (validIds.length === 0) {
      dispatch({
        type: FETCH_FILTERED_CONTAINERS_SUCCESS,
        payload: { filteredContainers: [], query: { ids: [], total: 0 } },
      });
      return;
    }

    const {
      filteredContainers: { query, data },
    } = getState();

    const isSameQuery =
      JSON.stringify(query.ids) === JSON.stringify(validIds);
    const isDataComplete = data.length === query.total;

    if (!isSameQuery || !isDataComplete) {
      dispatch({ type: FETCH_FILTERED_CONTAINERS });

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/containers?page=1${getIdParams(validIds)}`,
          {
            headers: {
              "Content-Type": "application/ld+json; charset=utf-8",
              Accept: "application/ld+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const members = response.data["hydra:member"] || [];
        const total = response.data["hydra:totalItems"] || 0;

        dispatch({
          type: FETCH_FILTERED_CONTAINERS_SUCCESS,
          payload: { filteredContainers: members, query: { ids: validIds, total } },
        });

        const next = response.data["hydra:view"]?.["hydra:next"];
        if (next) dispatch(getNextFilteredContainers(next));
      } catch (err) {
        dispatch({ type: FETCH_FILTERED_CONTAINERS_ERROR, payload: err.message });
      }
    }
  };

export const getNextFilteredContainers = (nextUrl) => async (dispatch) => {
  dispatch({ type: FETCH_NEXT_FILTERED_CONTAINERS });

  try {
    const response = await axios.get(nextUrl, {
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Accept: "application/ld+json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    dispatch({
      type: FETCH_NEXT_FILTERED_CONTAINERS_SUCCESS,
      payload: response.data["hydra:member"] || [],
    });

    const next = response.data["hydra:view"]?.["hydra:next"];
    if (next) dispatch(getNextFilteredContainers(next));
  } catch (err) {
    dispatch({ type: FETCH_FILTERED_CONTAINERS_ERROR, payload: err.message });
  }
};

export const updateFilteredContainer = (
  container,
  allowHideModal = true
) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_FILTERED_CONTAINER });

  const { modal } = getState();

  try {
    const id = container["@id"].split("/").pop();
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/containers/${id}`,
      container,
      {
        headers: {
          "Content-Type": "application/ld+json",
          Accept: "application/ld+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    dispatch({ type: UPDATE_FILTERED_CONTAINER_SUCCESS, payload: response.data });

    if (allowHideModal) {
      if (modal.childModal) dispatch(hideChildModal());
      else dispatch(hideModal());
    }
  } catch (err) {
    dispatch({ type: UPDATE_FILTERED_CONTAINER_ERROR, payload: err.message });
  }
};

export const loadFilteredContainers = () => ({ type: FETCH_FILTERED_CONTAINERS });
export const loadNextFilteredContainers = () => ({ type: FETCH_NEXT_FILTERED_CONTAINERS });
export const setFilteredContainersError = (msg) => ({ type: FETCH_FILTERED_CONTAINERS_ERROR, payload: msg });

/***********/
/* REDUCER */
/***********/
const filteredContainerReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_FILTERED_CONTAINERS:
      return { ...state, loading: true, error: "" };
    case FETCH_FILTERED_CONTAINERS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.filteredContainers,
        query: payload.query,
      };
    case FETCH_FILTERED_CONTAINERS_ERROR:
      return { ...state, loading: false, error: payload };
    case FETCH_NEXT_FILTERED_CONTAINERS:
      return { ...state, loading: true, error: "" };
    case FETCH_NEXT_FILTERED_CONTAINERS_SUCCESS: {
      const existingIds = new Set(state.data.map((c) => c["@id"]));
      const uniqueNewItems = payload.filter((c) => !existingIds.has(c["@id"]));
      return { ...state, loading: false, data: [...state.data, ...uniqueNewItems] };
    }
    case UPDATE_FILTERED_CONTAINER:
      return { ...state, loading: true, error: "" };
    case UPDATE_FILTERED_CONTAINER_SUCCESS:
      return {
        ...state,
        loading: false,
        data: state.data.map((c) =>
          c["@id"] === payload["@id"] ? payload : c
        ),
      };
    case UPDATE_FILTERED_CONTAINER_ERROR:
      return { ...state, loading: false, error: payload };
    default:
      return state;
  }
};

export default filteredContainerReducer;
